import { useRef, useEffect, useState } from 'react';
import { ArrowRight, Plane, Ship, Palmtree, MessageCircle } from 'lucide-react';

const WHATSAPP_RECOGNITION = 'https://wa.me/5551996777625?text=Ol%C3%A1!%20Tenho%20interesse%20em%20me%20tornar%20um%20licenciado%20iGreen%20e%20gostaria%20de%20entender%20como%20funciona%20a%20oportunidade%20e%20a%20renda%20recorrente.';

const trips = [
  {
    icon: Plane,
    title: 'Estados Unidos',
    description: 'Experiência internacional com destinos icônicos nos Estados Unidos para licenciados elegíveis.',
    image: `${__BASE_PATH__}assets/viagem-eua.jpg`,
    label: 'EUA',
  },
  {
    icon: Ship,
    title: 'Cruzeiro iGreen 2026',
    description: 'Navegue em alto mar com exclusividade e celebre suas conquistas no Cruzeiro iGreen.',
    image: `${__BASE_PATH__}assets/viagem-cruzeiro.jpg`,
    label: 'Cruzeiro',
  },
  {
    icon: Palmtree,
    title: 'Caribe',
    description: 'Praias paradisíacas e momentos inesquecíveis no Caribe para os melhores resultados.',
    image: `${__BASE_PATH__}assets/viagem-caribe.jpg`,
    label: 'Caribe',
  },
];

function TripCard({ trip, index }: { trip: typeof trips[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl overflow-hidden border border-background-200/70 shadow-lg transition-all duration-500 hover:-translate-y-1 hover:shadow-xl ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      {/* Imagem + badge */}
      <div className="relative aspect-video overflow-hidden bg-background-200">
        <img
          src={trip.image}
          alt={trip.title}
          className="h-full w-full object-contain"
          width="1600"
          height="900"
          loading="lazy"
        />
        {/* Label badge */}
        <div className="absolute left-1/2 top-4 flex -translate-x-1/2 items-center gap-2 rounded-lg bg-primary-500/90 px-3 py-1.5 text-xs font-bold text-white backdrop-blur-sm md:left-4 md:translate-x-0">
          <trip.icon className="w-3.5 h-3.5" />
          {trip.label}
        </div>
      </div>

      {/* Texto embaixo */}
      <div className="bg-background-950 p-5 text-center md:p-6 md:text-left">
        <h3 className="text-xl md:text-2xl font-bold text-white mb-1">{trip.title}</h3>
        <p className="text-sm text-foreground-300 leading-relaxed">{trip.description}</p>
      </div>
    </div>
  );
}

export default function RecognitionSection() {
  return (
    <section id="experiencias" className="immersive-panel bg-background-100">
      <div className="immersive-inner">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 border border-accent-200 text-accent-700 text-sm font-semibold mb-5">
            <Plane className="w-4 h-4" />
            Reconhecimento
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-tight">
            Resultados que se transformam em{' '}
            <span className="text-gradient">experiências</span>
          </h2>
          <p className="mt-5 text-lg text-foreground-600 leading-relaxed">
            Viagens e premiações conquistadas por licenciados elegíveis nas campanhas de reconhecimento da iGreen.
          </p>
        </div>

        {/* Trip cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {trips.map((trip, i) => (
            <TripCard key={trip.title} trip={trip} index={i} />
          ))}
        </div>

        {/* CTA + Disclaimer */}
        <div className="mt-10 md:mt-12 text-center">
          <a
            href={WHATSAPP_RECOGNITION}
            target="_blank"
            rel="noreferrer"
            className="btn-primary text-sm md:text-base px-6 md:px-8 py-3.5 md:py-4 group inline-flex"
          >
            <MessageCircle className="w-5 h-5" />
            Conhecer a oportunidade
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </a>
          <p className="mt-4 text-xs text-foreground-500 max-w-xl mx-auto leading-relaxed">
            Resultados variam conforme atividade, clientes ativos e regras comerciais. Viagens e premiações dependem dos critérios de cada campanha.
          </p>
        </div>
      </div>
    </section>
  );
}
